import { useNavigate } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { GlassWater, ArrowRight, Wrench, Tag } from "lucide-react";

interface AppCard {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  route: string;
  status: "live" | "coming-soon";
}

const apps: AppCard[] = [
  {
    id: "bottling",
    name: "Bottling Scheduler",
    description: "Client bottling requests, SKU management, production planning, and daily sheet generation.",
    icon: GlassWater,
    route: "/apps/bottling",
    status: "live",
  },
  {
    id: "maintenance",
    name: "Parts",
    description: "Asset registry, parts inventory, stock by location, MX work orders, and photo documentation.",
    icon: Wrench,
    route: "/parts",
    status: "live",
  },
  {
    id: "case-labels",
    name: "Case Label Printer",
    description: "Design, save, and print case labels for client wines in 4×4\" and 2×4\" formats.",
    icon: Tag,
    route: "/apps/case-labels",
    status: "live",
  },
];

const iconStyle: Record<string, string> = {
  bottling: "bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400",
  maintenance: "bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400",
  "case-labels": "bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400",
};

export default function Apps() {
  const navigate = useNavigate();

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="px-8 py-5 flex items-center gap-4">
          <SidebarTrigger className="-ml-2" />
          <h1 className="text-2xl font-semibold tracking-tight">Apps</h1>
        </div>
      </div>

      {/* App Grid */}
      <div className="px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {apps.map((app) => {
            const isLive = app.status === "live";
            return (
              <button
                key={app.id}
                onClick={() => isLive && navigate(app.route)}
                disabled={!isLive}
                className={`group relative text-left rounded-xl border bg-card p-6 transition-all duration-200 ${
                  isLive
                    ? "cursor-pointer hover:shadow-md hover:border-foreground/15 hover:-translate-y-0.5 active:translate-y-0"
                    : "cursor-default opacity-60"
                }`}
              >
                {/* Icon */}
                <div className={`mb-4 inline-flex items-center justify-center rounded-lg p-2.5 ${iconStyle[app.id] || "bg-muted text-foreground"}`}>
                  <app.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>

                {/* Content */}
                <div className="mb-4">
                  <div className="flex items-center gap-2.5">
                    <h2 className="text-[15px] font-semibold tracking-tight">{app.name}</h2>
                    {!isLive && (
                      <span className="rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Soon
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                    {app.description}
                  </p>
                </div>

                {/* Launch arrow */}
                {isLive && (
                  <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground group-hover:text-foreground transition-colors">
                    Open
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
